const ACCESS_KEYS = Object.freeze([
  "fullAccess",
  "dashboard",
  "profile",
  "admin",
  "clients",
  "employees",
  "invoices",
  "scheduling",
  "proof_of_service",
  "issues",
  "supplies",
  "notifications",
  "alerts",
  "messaging",
  "reports_standard",
  "reports_advanced",
  "client_portal",
  "client_notifications",
  "sms",
  "push",
  "settings",
  "billing",
  "support",
  "accounts",
]);

const ROUTE_TO_ACCESS_KEY = Object.freeze({
  DASHBOARD: "dashboard",
  PROFILE: "profile",
  ADMIN: "admin",
  CREATE_ADMIN: "admin",
  MANAGE_ADMIN: "admin",
  CLIENTS: "clients",
  CREATE_CLIENT: "clients",
  MANAGE_CLIENT: "clients",
  CLIENT_SUMMARY: "clients",
  EMPLOYEE: "employees",
  CREATE_EMPLOYEE: "employees",
  MANAGE_EMPLOYEE: "employees",
  MANAGE_GROUPS: "employees",
  INACTIVE_EMPLOYEE: "employees",
  INVOICE: "invoices",
  CREATE_INVOICE: "invoices",
  MANAGE_INVOICE: "invoices",
  CRT_INV: "invoices",
  MNG_INV: "invoices",
  APPOINTMENTS: "scheduling",
  CREATE_APPOINTMENT: "scheduling",
  MANAGE_APPOINTMENT: "scheduling",
  CRT_APPT: "scheduling",
  MNG_APPT: "scheduling",
  ISSUES: "issues",
  MANAGE_ISSUES: "issues",
  ARCHIVE_ISSUES: "issues",
  MNG_ISSUES: "issues",
  ARCH_ISSUES: "issues",
  SUPPLIES: "supplies",
  MANAGE_SUPPLIES: "supplies",
  ARCHIVE_SUPPLIES: "supplies",
  MNG_SUPPLIES: "supplies",
  ARCH_SUPPLIES: "supplies",
  REPORTS: "reports_standard",
  STANDARD_REPORTS: "reports_standard",
  ADVANCED_REPORTS: "reports_advanced",
  ADV_CLIENT_REPORTS: "reports_advanced",
  PROOF_OF_SERVICE_REPORT: "proof_of_service",
  ADV_APPOINTMENT_SCHEDULE_HEALTH: "reports_advanced",
  ADV_ISSUE_RESOLUTION_REPORTS: "reports_advanced",
  ADV_TEAM_REPORTS: "reports_advanced",
  ADV_TIME_ATTENDANCE_REPORTS: "reports_advanced",
  ADV_EMPLOYEE_SUMMARY_REPORTS: "reports_advanced",
  MESSAGE_BOARD: "messaging",
  LIVE_CHAT: "messaging",
  CREATE_CHANNEL: "messaging",
  MANAGE_CHANNEL: "messaging",
  CRT_CHANNELS: "messaging",
  MNG_CHANNELS: "messaging",
  CHAT_MEDIA: "messaging",
  NOTIFICATION: "notifications",
  CREATE_NOTIFICATION: "notifications",
  MANAGE_NOTIFICATION: "notifications",
  ALERTS: "alerts",
  ALL_ALERTS: "alerts",
  CLIENT_ALERTS: "alerts",
  EMPLOYEE_ALERTS: "alerts",
  EMP_ALERTS: "alerts",
  SUPPLIES_ALERTS: "alerts",
  ISSUES_ALERTS: "alerts",
  SYSTEM_ALERTS: "alerts",
  CLIENT_PORTAL: "client_portal",
  SMS_NOTIFICATION: "sms",
  PUSH: "push",
  SETTINGS: "settings",
  APPOINTMENT_SETTINGS: "settings",
  NOTIFICATION_SETTINGS: "settings",
  ISSUES_SETTINGS: "settings",
  CLIENT_SETTINGS: "settings",
  SUPPLY_SETTINGS: "settings",
  BILLING: "billing",
  UPGRADE: "billing",
  SUPPORT: "support",
  CREATE_SUPPORT: "support",
  SUPPORT_DETAILS: "support",
  TECHNICAL_SUPPORT: "support",
  MANAGE_SUPPORT: "support",
  ARCHIVE_SUPPORT: "support",
  ARCHIVE_SUPPORT_DETAILS: "support",
  ACCOUNTS: "accounts",
});

const TRUTHY_STRINGS = new Set([
  "true",
  "yes",
  "on",
  "full",
  "partial",
  "limited",
  "text_only",
  "push_only",
  "basic",
  "standard",
  "unlimited",
  "predictive",
  "advanced",
  "simple",
]);

const FALSY_STRINGS = new Set([
  "false",
  "no",
  "off",
  "none",
  "disabled",
  "not_allowed",
  "notallowed",
]);

function normalizeValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeValue(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = normalizeValue(value[key]);
      return acc;
    }, {});
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return "";
    const num = Number(trimmed);
    if (Number.isFinite(num) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
      return num;
    }
    return trimmed;
  }
  return value;
}

function getPathValue(source, path) {
  if (!source || !path) return undefined;
  return String(path)
    .split(".")
    .reduce((curr, key) => (curr && curr[key] !== undefined ? curr[key] : undefined), source);
}

function toBooleanAccess(value) {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  if (typeof value === "number") return value > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (FALSY_STRINGS.has(normalized)) return false;
    if (TRUTHY_STRINGS.has(normalized)) return true;
  }
  return Boolean(value);
}

export function deriveWebPortalAccessFromFeatures(features = {}) {
  const core = features?.core_operations || {};
  const reports = features?.reports || {};
  const advanced = features?.advanced || {};
  const clientFacing = features?.client_facing || {};
  const communications = features?.communications || {};
  const clientManager = features?.client_manager || {};
  const supplyManager = features?.supply_manager || {};
  const issueManager = features?.issue_manager || {};
  const appointments = features?.appointments || {};
  const employeeManager = features?.employee_manager || {};

  const hasReportsStandard = toBooleanAccess(core?.reports) || toBooleanAccess(reports?.standard);
  const hasAdvancedReports = toBooleanAccess(advanced?.advanced_reporting) || toBooleanAccess(reports?.advanced);
  const hasNotifications = toBooleanAccess(core?.notifications) || toBooleanAccess(communications?.push);
  const hasMessaging = toBooleanAccess(core?.team_chat) || toBooleanAccess(communications?.chat);

  return {
    fullAccess: false,
    dashboard: true,
    profile: true,
    admin: true,
    clients: true,
    employees: toBooleanAccess(employeeManager?.manage) || true,
    invoices: true,
    scheduling: toBooleanAccess(core?.scheduling) || toBooleanAccess(appointments?.drag_drop_calendar),
    proof_of_service: toBooleanAccess(core?.proof_of_service) || toBooleanAccess(features?.proof_of_service?.company_performance),
    issues: toBooleanAccess(core?.issues) || toBooleanAccess(issueManager?.create),
    supplies: toBooleanAccess(core?.supplies) || toBooleanAccess(supplyManager?.requests),
    notifications: hasNotifications,
    alerts: hasNotifications || toBooleanAccess(appointments?.real_time_alerts),
    messaging: hasMessaging,
    reports_standard: hasReportsStandard,
    reports_advanced: hasAdvancedReports,
    client_portal: toBooleanAccess(core?.client_portal) || toBooleanAccess(clientManager?.client_portal),
    client_notifications: toBooleanAccess(clientFacing?.client_notifications),
    sms: toBooleanAccess(communications?.sms) || toBooleanAccess(clientFacing?.sms_alerts),
    push: toBooleanAccess(communications?.push) || hasNotifications,
    settings: true,
    billing: true,
    support: true,
    accounts: true,
  };
}

export function normalizePlan(plan = {}) {
  const normalizedFeatures = normalizeValue(plan?.features || {});
  const normalizedLimits = normalizeValue(plan?.limits || {});
  const derivedAccess = deriveWebPortalAccessFromFeatures(normalizedFeatures);
  const givenAccess = normalizeValue(plan?.webPortalAccess || {});

  const normalizedAccess = ACCESS_KEYS.reduce((acc, key) => {
    const sourceValue = givenAccess[key] !== undefined ? givenAccess[key] : derivedAccess[key];
    acc[key] = toBooleanAccess(sourceValue);
    return acc;
  }, {});

  return {
    ...plan,
    features: normalizedFeatures,
    limits: normalizedLimits,
    webPortalAccess: normalizedAccess,
  };
}

export function normalizePlanCollection(plans = []) {
  if (!Array.isArray(plans)) return [];
  return plans.map((plan) => normalizePlan(plan));
}

export function hasFeature(plan, path) {
  const normalizedPlan = normalizePlan(plan);
  const value = getPathValue(normalizedPlan?.features, path);
  return toBooleanAccess(value);
}

export function hasAccess(plan, key) {
  const normalizedPlan = normalizePlan(plan);
  if (normalizedPlan?.webPortalAccess?.fullAccess === true) return true;
  return toBooleanAccess(normalizedPlan?.webPortalAccess?.[key]);
}

export function getAccessKeyForRoute(routeKey) {
  if (!routeKey) return null;
  return ROUTE_TO_ACCESS_KEY[routeKey] || null;
}

